/* eslint-disable react/jsx-no-undef */
import React from 'react';
import { Button, Collapse, FormGroup, Input, Label } from 'reactstrap';
import i18n from '@/utils/i18n';
import { useGetUnlinkedOrderFulfillmentPicturesQuery } from '../../../graphql/queries/getUnlinkedOrderFulfillmentPictures.generated';
import { EditItem } from '../../../components/Layout';
import { ContentSection } from '../../../components/Layout/ContentSection';
import { FaAngleDown, FaAngleRight, FaPlus } from 'react-icons/fa';
import DateTimePicker from '../../../components/DateTimePicker';
import UploadOrderFulfillmentPicturesModal from './Components/UploadOrderFulfillmentPicturesModal';
import {
  ORDER_FULFILLMENT_PICTURES_PAGE_SIZE,
  OrderFulfillmentPicturesTable,
} from './Components/OrderFulfillmentPicturesTable';

const UnlinkedOrderFulfillmentPictures: React.FC = () => {
  const [filterIsOpen, setFilterIsOpen] = React.useState(false);

  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const [comment, setComment] = React.useState<string | null>(null);
  const [createdBy, setCreatedBy] = React.useState<string | null>(null);
  const [isValidUUID, setIsValidUUID] = React.useState(false);
  const [showUploadModal, setShowUploadModal] = React.useState(false);
  const [page, setPage] = React.useState(1);

  const { data, loading, error, refetch } =
    useGetUnlinkedOrderFulfillmentPicturesQuery({
      fetchPolicy: 'network-only',
      variables: {
        filter: {
          start_date: startDate?.toISOString(),
          end_date: endDate?.toISOString(),
          comment: (comment || '').trim().length > 0 ? comment : null,
          created_by_user_id: isValidUUID ? createdBy : null,
        },
        skip: 0,
        take: ORDER_FULFILLMENT_PICTURES_PAGE_SIZE,
      },
    });

  function validateUUID(value: string) {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    setIsValidUUID(uuidRegex.test(value));
  }

  function applyFilter() {
    refetch({
      skip: 0,
      take: ORDER_FULFILLMENT_PICTURES_PAGE_SIZE,
      filter: {
        start_date: startDate?.toISOString(),
        end_date: endDate?.toISOString(),
        comment: (comment || '').trim().length > 0 ? comment : null,
        created_by_user_id: isValidUUID ? createdBy : null,
      },
    });
    setPage(1);
  }

  return (
    <ContentSection
      title={i18n.t('OrderFulfillmentPictures:unlinkedPictures')}
      collapsable={false}
    >
      <Button
        className="float-end ms-2 mt-2 mb-2"
        onClick={() => setShowUploadModal(true)}
      >
        <FaPlus className="me-1" /> {i18n.t('OrderFulfillmentPictures:upload')}
      </Button>
      <br style={{ clear: 'both' }} />
      <EditItem title={i18n.t('OrderFulfillmentPictures:filter')}>
        <Button
          onClick={() => setFilterIsOpen(!filterIsOpen)}
          className="float-end ms-2 mt-2 mb-2"
        >
          {filterIsOpen ? <FaAngleDown /> : <FaAngleRight />}
        </Button>
        <Collapse isOpen={filterIsOpen}>
          <form>
            <FormGroup>
              <Label for="startDate">
                {i18n.t('OrderFulfillmentPictures:startDate')}
              </Label>
              <DateTimePicker
                dateTime={startDate}
                setDateTime={setStartDate}
                key="startDate"
              />
            </FormGroup>
            <FormGroup>
              <Label for="endDate">
                {i18n.t('OrderFulfillmentPictures:endDate')}
              </Label>
              <DateTimePicker
                dateTime={endDate}
                setDateTime={setEndDate}
                key="endDate"
              />
            </FormGroup>
            <FormGroup>
              <Label for="createdBy">
                {i18n.t('OrderFulfillmentPictures:createdBy')}
              </Label>
              <Input
                type="text"
                name="createdBy"
                id="createdBy"
                className={'form-control ' + (!isValidUUID ? 'is-invalid' : '')}
                value={createdBy || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setCreatedBy(value);
                  validateUUID(value);
                }}
              />
            </FormGroup>
            <FormGroup>
              <Label for="comment">
                {i18n.t('OrderFulfillmentPictures:comment')}
              </Label>
              <Input
                className="form-control"
                type="text"
                name="comment"
                id="comment"
                value={comment || ''}
                onChange={(e) => setComment(e.target.value)}
              />
            </FormGroup>
          </form>
          <Button className="float-end" onClick={() => applyFilter()}>
            {i18n.t('Common:apply')}
          </Button>
        </Collapse>
      </EditItem>
      <EditItem title={i18n.t('OrderFulfillmentPictures:results')}>
        <OrderFulfillmentPicturesTable
          error={error}
          loading={loading}
          loadMore={(skip, take) => refetch({ skip, take })}
          pictures={data?.getUnlinkedOrderFulfillmentPictures.pictures || []}
          total={data?.getUnlinkedOrderFulfillmentPictures.total || 0}
          page={page}
          showLinkButton={true}
          showUnlinkButton={false}
          setPage={setPage}
        />
      </EditItem>
      <UploadOrderFulfillmentPicturesModal
        isOpen={showUploadModal}
        setIsOpen={(newValue) => setShowUploadModal(newValue)}
        onUploadComplete={() => refetch()}
      />
    </ContentSection>
  );
};

export default UnlinkedOrderFulfillmentPictures;
